import * as React from 'react';

import { useQuery } from '@apollo/client';

import { DEFAULT_DEGREE_COLLECTION_QUERY_INPUTS } from 'bundles/browse-common/constants/degreeCollection';
import DegreeCollectionContext from 'bundles/browse-common/contexts/DegreeCollectionContext';
import BrowseDegreeCollection from 'bundles/browse-common/queries/BrowseDegreeCollection.graphql';
import type {
  BrowseDegreeCollection_CollectionFragment as BrowseDegreeCollectionFragment,
  BrowseDegreeCollectionQuery,
  BrowseDegreeCollectionQueryVariables,
} from 'bundles/browse-common/queries/__generated__/BrowseDegreeCollection';
import type { DegreeCollectionSet } from 'bundles/browse-common/types/degreeCollection';
import type { KnownDomainSlugs } from 'bundles/browse-common/types/domain';
import { ProductVariantNames } from 'bundles/browse/constants';
import type { CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';

import _t from 'i18n!nls/browse-common';

type Props = {
  domainSlug?: KnownDomainSlugs;
  children: React.ReactNode;
};

const useDegreeCollectionQuery = (domainId?: string) => {
  const domainIdWithHealthFix = domainId === 'health' ? 'life-sciences' : domainId;
  return useQuery<BrowseDegreeCollectionQuery, BrowseDegreeCollectionQueryVariables>(BrowseDegreeCollection, {
    variables: {
      input: {
        ...DEFAULT_DEGREE_COLLECTION_QUERY_INPUTS,
        domains: domainIdWithHealthFix ? [domainIdWithHealthFix] : null,
      },
    },
    context: {
      clientName: 'gatewayGql',
    },
    errorPolicy: 'all',
  });
};

const transformToCollectionEntity = (entry: BrowseDegreeCollectionFragment | null): CollectionEntity => {
  return {
    id: entry?.id ?? '',
    name: entry?.name ?? '',
    slug: entry?.slug ?? '',
    imageUrl: entry?.imageUrl ?? '',
    link: '/degrees/' + entry?.slug,
    productType: entry?.productVariant === ProductVariantNames.PostgraduateDiploma ? 'POSTGRADUATE DIPLOMA' : 'DEGREE',
    partners: entry?.partnerEntities
      ? entry?.partnerEntities.map((partner) => ({
          id: partner?.id ?? '',
          // Downstream ProductCardCollection uses `partnerLogos` property below to render the partner logos
          logo: '',
          name: partner?.name ?? '',
        }))
      : [],
    partnerLogos: entry?.partners.map((partner) => partner?.logo ?? ''),
  };
};

export const transformToDegreeCollectionSet = (
  collectionData: Array<BrowseDegreeCollectionFragment | null> | null | undefined
): DegreeCollectionSet => {
  const bachelors = collectionData?.filter((entry) => entry?.productVariant === ProductVariantNames.BachelorsDegree);
  const masters = collectionData?.filter((entry) => entry?.productVariant === ProductVariantNames.MastersDegree);
  const postgraduate = collectionData?.filter(
    (entry) => entry?.productVariant === ProductVariantNames.PostgraduateDiploma
  );

  return {
    all: {
      collectionId: 'all',
      title: _t('All'),
      entities: collectionData?.map(transformToCollectionEntity) ?? [],
    },
    ...(bachelors &&
      bachelors.length > 0 && {
        bachelors: {
          collectionId: 'bachelors',
          title: _t('Bachelors'),
          entities: bachelors.map(transformToCollectionEntity),
        },
      }),
    ...(masters &&
      masters.length > 0 && {
        masters: {
          collectionId: 'masters',
          title: _t('Masters'),
          entities: masters.map(transformToCollectionEntity),
        },
      }),
    ...(postgraduate &&
      postgraduate.length > 0 && {
        postgraduate: {
          collectionId: 'postgraduate',
          title: _t('Postgraduate'),
          entities: postgraduate.map(transformToCollectionEntity),
        },
      }),
  };
};

const DegreeCollectionProvider = (props: Props) => {
  const { domainSlug, children } = props;

  const domainId = domainSlug;

  const { data, loading } = useDegreeCollectionQuery(domainId);
  const collectionSet = transformToDegreeCollectionSet(data?.PremiumProductCollections?.queryCollection);

  return (
    <DegreeCollectionContext.Provider value={{ collectionSet, isLoading: loading }}>
      {children}
    </DegreeCollectionContext.Provider>
  );
};

export default DegreeCollectionProvider;
